import { useState, useEffect, useCallback } from 'react';

const WEBVONK_URL = 'https://swxfaqoftmwphbparotf.supabase.co';
const WEBVONK_ANON_KEY = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InN3eGZhcW9mdG13cGhicGFyb3RmIiwicm9sZSI6ImFub24iLCJpYXQiOjE3Nzk1MjY1OTQsImV4cCI6MjA5NTEwMjU5NH0.IZZAiD-ndE_CvkuY3E44N8Sm7u1EXnsYaYnCda6R-v4';
const INACTIVITY_KEY = 'kmfc_pin_inactivity_ms';
const ATTEMPTS_KEY = 'kmfc_pin_attempts';
const LOCKOUT_COUNT_KEY = 'kmfc_pin_lockout_count';
const LOCKED_UNTIL_KEY = 'kmfc_pin_locked_until';
const MAX_ATTEMPTS = 5;
const DEFAULT_INACTIVITY_MS = 5 * 60 * 1000;

const getInactivityMs = () => {
  try { return parseInt(localStorage.getItem(INACTIVITY_KEY)) || DEFAULT_INACTIVITY_MS; } catch { return DEFAULT_INACTIVITY_MS; }
};

const getLockoutDuration = (lockoutCount) => 30 * Math.pow(2, lockoutCount) * 1000;

const getUser = () => { try { return JSON.parse(localStorage.getItem('kmfc_user') || '{}'); } catch { return {}; } };
const getAuthToken = () => getUser().access_token;

const rpc = async (fn, params) => {
  const res = await fetch(`${WEBVONK_URL}/rest/v1/rpc/${fn}`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${getAuthToken()}`,
      'apikey': WEBVONK_ANON_KEY,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(params),
  });
  return res;
};

export function usePinLock() {
  const [hasPin, setHasPin] = useState(false);
  const [locked, setLocked] = useState(false);
  const [loading, setLoading] = useState(true);
  const [inactivityMs, setInactivityMs] = useState(getInactivityMs);
  const [failedAttempts, setFailedAttempts] = useState(() => parseInt(localStorage.getItem(ATTEMPTS_KEY) || '0'));
  const [lockedUntil, setLockedUntil] = useState(() => parseInt(localStorage.getItem(LOCKED_UNTIL_KEY) || '0'));

  const user = getUser();
  const userId = user.id;
  const companySlug = user.company_slug;

  const checkHasPin = useCallback(async () => {
    if (!userId || !companySlug) { setLoading(false); return; }
    const res = await rpc('has_user_pin', { p_user_id: userId, p_company_slug: companySlug });
    const result = res.ok ? await res.json() : false;
    setHasPin(!!result);
    if (result) setLocked(true);
    setLoading(false);
  }, [userId, companySlug]);

  useEffect(() => { checkHasPin(); }, [checkHasPin]);

  const setInactivity = useCallback((ms) => {
    localStorage.setItem(INACTIVITY_KEY, ms);
    setInactivityMs(ms);
  }, []);

  useEffect(() => {
    if (!hasPin || locked) return;
    const LAST_ACTIVE_KEY = 'kmfc_pin_last_active';
    const stamp = () => localStorage.setItem(LAST_ACTIVE_KEY, Date.now());
    stamp();
    const events = ['mousedown', 'keydown', 'touchstart', 'scroll'];
    events.forEach(e => window.addEventListener(e, stamp, { passive: true }));
    const interval = setInterval(() => {
      const last = parseInt(localStorage.getItem(LAST_ACTIVE_KEY) || '0');
      if (Date.now() - last >= inactivityMs) setLocked(true);
    }, 1000);
    return () => {
      clearInterval(interval);
      events.forEach(e => window.removeEventListener(e, stamp));
    };
  }, [hasPin, locked, inactivityMs]);

  const unlock = useCallback(async (pin) => {
    const now = Date.now();
    const until = parseInt(localStorage.getItem(LOCKED_UNTIL_KEY) || '0');
    if (now < until) return { ok: false, lockedUntil: until };

    const res = await rpc('verify_user_pin', { p_user_id: userId, p_company_slug: companySlug, p_pin: pin });
    const ok = res.ok ? await res.json() : false;

    if (ok) {
      localStorage.removeItem(ATTEMPTS_KEY);
      localStorage.removeItem(LOCKED_UNTIL_KEY);
      localStorage.removeItem(LOCKOUT_COUNT_KEY);
      setFailedAttempts(0);
      setLockedUntil(0);
      setLocked(false);
      return { ok: true };
    }

    const prev = parseInt(localStorage.getItem(ATTEMPTS_KEY) || '0');
    const next = prev + 1;
    localStorage.setItem(ATTEMPTS_KEY, next);
    setFailedAttempts(next);

    if (next >= MAX_ATTEMPTS) {
      const lockoutCount = parseInt(localStorage.getItem(LOCKOUT_COUNT_KEY) || '0');
      const duration = getLockoutDuration(lockoutCount);
      const until = Date.now() + duration;
      localStorage.setItem(LOCKED_UNTIL_KEY, until);
      localStorage.setItem(LOCKOUT_COUNT_KEY, lockoutCount + 1);
      localStorage.setItem(ATTEMPTS_KEY, '0');
      setLockedUntil(until);
      setFailedAttempts(0);
      return { ok: false, lockedUntil: until };
    }

    return { ok: false, remaining: MAX_ATTEMPTS - next };
  }, [userId, companySlug]);

  const verifyPin = useCallback(async (pin) => {
    const res = await rpc('verify_user_pin', { p_user_id: userId, p_company_slug: companySlug, p_pin: pin });
    const ok = res.ok ? await res.json() : false;
    return !!ok;
  }, [userId, companySlug]);

  const setPin = useCallback(async (pin) => {
    const res = await rpc('set_user_pin', { p_user_id: userId, p_company_slug: companySlug, p_pin: pin });
    if (res.ok) { setHasPin(true); }
    return res.ok;
  }, [userId, companySlug]);

  const removePin = useCallback(async () => {
    const res = await rpc('remove_user_pin', { p_user_id: userId, p_company_slug: companySlug });
    if (res.ok) { setHasPin(false); setLocked(false); }
    return res.ok;
  }, [userId, companySlug]);

  return { hasPin, locked, loading, unlock, verifyPin, setPin, removePin, checkHasPin, inactivityMs, setInactivity, failedAttempts, lockedUntil, maxAttempts: MAX_ATTEMPTS };
}
